import React from 'react';
import { Wand2, Sparkles } from 'lucide-react';
import type { TranslationKeys } from '@/core/i18n/translations';

export type HomeAppSection = 'builder' | 'explore';

export interface WorkoutHomeSectionTabsProps {
  section: HomeAppSection;
  setSection: (next: HomeAppSection) => void;
  t: TranslationKeys;
}

const pillClass = (active: boolean) =>
  [
    'min-h-[44px] shrink-0 inline-flex items-center gap-2 rounded-full',
    'px-4 sm:px-5 py-2.5 text-sm font-semibold touch-manipulation',
    'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
    active
      ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30 ring-1 ring-orange-400/30'
      : 'text-slate-700 dark:text-slate-200 hover:bg-white/80 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white',
  ].join(' ');

/**
 * Top-level "Pills" navigation between Builder / Explore.
 * Visual contract: elevated rounded pills with a vivid gradient active state — deliberately
 * different from the underline-style sport sub-tabs to make the page hierarchy obvious.
 */
export function WorkoutHomeSectionTabs({ section, setSection, t }: WorkoutHomeSectionTabsProps) {
  return (
    <nav
      className="mb-10 sm:mb-12"
      data-testid="home-section-tabs"
      aria-label="Primary section navigation"
    >
      <div
        role="tablist"
        className="inline-flex max-w-full flex-wrap gap-1 p-1.5 rounded-full bg-slate-200/90 dark:bg-slate-800/90 border border-slate-300/70 dark:border-slate-700/80 shadow-sm backdrop-blur-sm"
      >
        <button
          type="button"
          role="tab"
          aria-selected={section === 'builder'}
          data-testid="home-section-builder"
          onClick={() => setSection('builder')}
          className={pillClass(section === 'builder')}
        >
          <Wand2 className="w-4 h-4 shrink-0" aria-hidden />
          {t.tabBuilder}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={section === 'explore'}
          data-testid="home-section-explore"
          onClick={() => setSection('explore')}
          className={pillClass(section === 'explore')}
        >
          <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
          {t.tabExplore}
        </button>
      </div>
    </nav>
  );
}
